import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'
import { useApiWithIDToken } from '../../state/modules/common'
import { useNotifications } from '../../state/modules/notifications'
import { Variant } from '../../common/types'
import { useProgressToast } from './helpers/useProgressToast'

type DeleteChecklistDocumentReviewVariables = {
  documentId: number
  documentName: string
}

/**
 * Delete the most recent review for a given checklist document.
 *
 * @returns A UseMutationResult object for triggering the deletion.
 */
export const useDeleteChecklistDocumentReview = (): UseMutationResult<
  void,
  Error,
  DeleteChecklistDocumentReviewVariables
> => {
  const api = useApiWithIDToken()
  const queryClient = useQueryClient()
  const { send: sendNotification } = useNotifications()
  const { startProgress, endProgress } = useProgressToast()

  return useMutation<
    void,
    Error,
    DeleteChecklistDocumentReviewVariables,
    { toastId: string }
  >({
    mutationFn: ({ documentId, documentName }) =>
      deleteChecklistDocumentReview(api, documentId, documentName),
    onMutate: (vars) => ({
      toastId: startProgress(
        'Removing Review',
        `Removing review for ${vars.documentName}`
      ),
    }),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['siteRFPChecklists'],
        refetchType: 'active',
      })
      void queryClient.invalidateQueries({
        queryKey: ['siteRFPDocumentSummary'],
        refetchType: 'active',
      })
      sendNotification({
        title: 'Review Removed',
        message: `Review for ${vars.documentName} has been successfully removed.`,
        useRucksackUI: true,
      })
    },
    onError: (_data, vars) => {
      sendNotification({
        title: 'Remove Review Failed',
        message: `An error occurred while trying to remove the review for ${vars.documentName}. Please try again.`,
        variant: Variant.DANGER,
        useRucksackUI: true,
      })
    },
    onSettled: (_data, _error, _vars, context) => {
      endProgress(context?.toastId)
    },
  })
}

async function deleteChecklistDocumentReview(
  api: ReturnType<typeof useApiWithIDToken>,
  documentId: number,
  documentName: string
): Promise<void> {
  try {
    const url = `/documents/${documentId}/review`
    await api.delete(url)
  } catch (error) {
    throw new Error(
      `Failed to delete review for document ${documentName}: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}
